import { faChevronDown, faGasPump } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext } from 'react';
import { UserContext } from '../../context/UserProvider';
import { getFormattedDate } from '../../lib/dateUtils';
import { ChangeRequestStatus, FuelTransaction, FuelTransactionStatus } from '../../types/FuelTransaction';
import { Role } from '../../types/Role';
import { AccessLevel } from '../ui/AccessLevel';
import { List } from '../ui/List';
import { MeasurementUnit } from '../ui/MeasurementUnit';
import { Status, StatusMessageType } from '../ui/StatusMessage';
import { DeleteFuelTransaction } from './actions/DeleteFuelTransaction';
import { EditFuelTransaction } from './actions/EditFuelTransaction';
import { MarkFuelTransactionAsSubtituteCarUsage } from './actions/MarkFuelTransactionAsSubtituteCarUsage';
import { ReviewChangeRequest } from './actions/ReviewChangeRequest';
import { UnMarkFuelTransactionAsSubtituteCarUsage } from './actions/UnMarkFuelTransactionAsSubtituteCarUsage';

type FuelTransactionLineProps = {
    index: number;
    data: FuelTransaction;
    toggleOpen: (index: number) => void;
    isOpen?: boolean;
};

export const FuelTransactionLine = ({ data, toggleOpen, isOpen, index }: FuelTransactionLineProps) => {
    const user = useContext(UserContext);
    const isAdmin = user.userRole === Role.ADMIN;
    const shouldShowStatuses =
        data.isFromPreviousPeriod ||
        (isAdmin && data.status === FuelTransactionStatus.PENDING) ||
        data.status === FuelTransactionStatus.INACCURATE ||
        data.status === FuelTransactionStatus.FUEL_CONSUMPTION_INCONSISTENT;

    const handleClick = (e: React.MouseEvent) => {
        // Is not applicable when clicking on a link
        if (e.target instanceof HTMLAnchorElement) return;

        e.preventDefault();

        toggleOpen(index);
    };

    return (
        <details className="group relative rounded" open={isOpen} onClick={(e) => handleClick(e)}>
            <summary
                className={`flex flex-col gap-2 cursor-pointer group-open:cursor-default px-3 py-4 bg-secondary rounded group-open:rounded-bl-none group-open:rounded-br-none`}
            >
                <div className="flex items-center gap-2 w-full">
                    <FontAwesomeIcon icon={faGasPump} className="text-lg" />

                    <div className="text-base">
                        {`${data.transactionDate ? getFormattedDate(data.transactionDate) : 'Datum onbekend'} | ${data.kilometersTotal.amount} ${
                            data.kilometersTotal.unit
                        }`}
                    </div>

                    <FontAwesomeIcon icon={faChevronDown} className="text-lg ml-auto transition-all ease-in-out duration-200 group-open:rotate-180" />
                </div>

                {shouldShowStatuses && (
                    <div className="flex flex-wrap gap-1">
                        {data.isFromPreviousPeriod && <Status message="Laatste transactie van vorige periode" type={StatusMessageType.INFO} />}

                        {isAdmin && data.status === FuelTransactionStatus.PENDING && (
                            <Status message="Aangepast door gebruiker" type={StatusMessageType.WARNING} />
                        )}

                        {(data.status === FuelTransactionStatus.INACCURATE ||
                            data.status === FuelTransactionStatus.FUEL_CONSUMPTION_INCONSISTENT) && (
                            <Status message="Controle vereist" type={StatusMessageType.ERROR} />
                        )}
                    </div>
                )}
            </summary>

            <div className="gap-3 flex flex-col px-3 py-4 bg-white z-10 border-l-[1px] border-r-[1px] border-b-[1px] border-secondary rounded">
                <List>
                    <List.LinkItem
                        title="Kenteken auto:"
                        label={data.licensePlate || ''}
                        href={`https://ovi.rdw.nl/default.aspx?kenteken=${data.licensePlate}`}
                    />
                    <List.LinkItem
                        title="Locatie:"
                        label={data.gasStationName || ''}
                        href={`https://maps.google.nl/maps?hl=nl&q=${data.gasStationName}`}
                    />
                    <List.Item label="Product:" value={data.fuelType || ''} />
                    <List.Item label="Aantal:" value={data.litersOfFuel || ''} />

                    {isAdmin && data.changeRequest?.kilometersOld && data.changeRequest.kilometersNew ? (
                        <>
                            <List.Item
                                label="Kilometerstand:"
                                childrenAsValue={<MeasurementUnit measurement={data.changeRequest.kilometersOld} />}
                                valueClassName="line-through"
                            />
                            <List.Item
                                label="Kilometerstand (nieuw):"
                                childrenAsValue={<MeasurementUnit measurement={data.changeRequest.kilometersNew} />}
                                labelClassName="text-active"
                                valueClassName="text-active"
                            />
                        </>
                    ) : (
                        <List.Item
                            label="Kilometerstand:"
                            labelClassName={`${data.status === FuelTransactionStatus.INACCURATE ? 'text-red-700' : ''}`}
                            valueClassName={`${data.status === FuelTransactionStatus.INACCURATE ? 'text-red-700' : ''}`}
                            childrenAsValue={<MeasurementUnit measurement={data.kilometersTotal} />}
                        />
                    )}

                    <List.Item
                        label="Verbruik:"
                        value={data.fuelConsumptionInKml || ''}
                        labelClassName={`${data.status === FuelTransactionStatus.FUEL_CONSUMPTION_INCONSISTENT ? 'text-red-700' : ''}`}
                        valueClassName={`${data.status === FuelTransactionStatus.FUEL_CONSUMPTION_INCONSISTENT ? 'text-red-700' : ''}`}
                    />

                    <List.Item label="Bedrag:" value={data.amountIncludingVAT || ''} />
                </List>

                <div className="w-full border-t-[1px] border-grey"></div>

                <div className="flex justify-end gap-1 items-center">
                    {data.changeRequest?.status === ChangeRequestStatus.PENDING && (
                        <AccessLevel role={Role.ADMIN}>
                            <ReviewChangeRequest fuelTransaction={data} />
                        </AccessLevel>
                    )}

                    <EditFuelTransaction fuelTransaction={data} />

                    {!data.withSubstituteCar && <MarkFuelTransactionAsSubtituteCarUsage fuelTransaction={data} />}
                    {data.withSubstituteCar && <UnMarkFuelTransactionAsSubtituteCarUsage fuelTransaction={data} />}

                    <AccessLevel role={Role.ADMIN}>
                        <DeleteFuelTransaction fuelTransaction={data} />
                    </AccessLevel>
                </div>
            </div>
        </details>
    );
};
